import { BRAND_PERSON_NAME } from "@/lib/brandConfig";

/**
 * Página de gracias en español tras el pago exitoso en Mercado Pago.
 * Usada por: /ar/invitacion/success
 *
 * Al montar, valida el payment_id contra la API de MP via
 * GET /api/mercadopago/validate-payment?payment_id=xxx
 *
 * Solo muestra el CTA si el pago fue aprobado. Standard → Telegram; VIP → portal exclusivo.
 * Lee ?plan=standard|vip para el plan correcto.
 */

import { AlertCircle, ArrowRight, CheckCircle, Clock, Crown, ExternalLink, Loader2, UserPlus } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BRAND_SOURCE_KEY } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

/**
 * Redirect URLs are now configured per brand via /dashboard/subscripciones/planes.
 * These env var fallbacks are used only when the API returns null (not yet configured).
 */
const FALLBACK_TELEGRAM_URL = import.meta.env.VITE_SUCCESS_REDIRECT_STANDARD as string | undefined;
const FALLBACK_VIP_URL = import.meta.env.VITE_SUCCESS_REDIRECT_VIP as string | undefined;

const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

type ValidationState = "loading" | "valid" | "pending" | "error";

type GraciasArProps = {
  /**
   * Plan por defecto cuando la URL no trae ?plan= (ej. success de exclusive-ar VIP).
   * VIP siempre empuja a Clerk registro → completar perfil → pending.
   */
  defaultPlan?: "standard" | "vip";
};

export default function GraciasAr({ defaultPlan = "standard" }: GraciasArProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const capturePremiumEvent = (event: string, props?: Record<string, unknown>) => {
    if (typeof window === "undefined") return;
    const posthog = (window as unknown as { posthog?: { capture?: (e: string, p?: Record<string, unknown>) => void } }).posthog;
    if (posthog && typeof posthog.capture === "function") {
      posthog.capture(event, { source: BRAND_SOURCE_KEY, ...props });
    }
  };

  // Plan e identificador (payment o subscription) vienen en la URL de success
  const paymentIdParam = searchParams.get("payment_id");
  const subscriptionIdParam =
    searchParams.get("subscription_id") ||
    searchParams.get("preapproval_id") ||
    searchParams.get("preapprovalId") ||
    searchParams.get("preapproval_plan_id");
  const planParam      = searchParams.get("plan") || searchParams.get("plan_tier");
  const flowTraceId    = searchParams.get("flow_trace_id") || undefined;
  const initialPlan: "standard" | "vip" =
    planParam === "vip" || defaultPlan === "vip" ? "vip" : "standard";

  const [validationState, setValidationState] = useState<ValidationState>("loading");
  const [validatedPlan, setValidatedPlan]     = useState<"standard" | "vip">(initialPlan);
  const [errorMessage, setErrorMessage]       = useState<string | null>(null);
  const [redirectUrl, setRedirectUrl]         = useState<string | null>(null);
  const [redirectLabel, setRedirectLabel]     = useState<string | null>(null);
  const [autoRedirected, setAutoRedirected]   = useState(false);

  useEffect(() => {
    const hasPaymentId = Boolean(paymentIdParam && /^\d+$/.test(paymentIdParam));
    const hasSubscriptionId = Boolean(subscriptionIdParam && subscriptionIdParam.trim().length > 0);

    // Hosted plan checkout (preapproval_plan) a veces vuelve a back_url sin ids.
    // VIP: no bloquear — invitamos a completar perfil Clerk igual (webhook ya crea la membresía).
    if (!hasPaymentId && !hasSubscriptionId) {
      capturePremiumEvent("premium_success_missing_identifier", {
        provider: "mercadopago",
        language: "es",
        flow_trace_id: flowTraceId,
        has_payment_id: false,
        has_subscription_id: false,
        soft_continue: initialPlan === "vip",
        plan_tier: initialPlan,
      });
      if (initialPlan === "vip") {
        setValidatedPlan("vip");
        setValidationState("valid");
        return;
      }
      setErrorMessage("No se encontró el identificador de pago/suscripción en la URL.");
      setValidationState("error");
      return;
    }

    const validate = async () => {
      try {
        capturePremiumEvent("premium_success_validation_started", {
          provider: "mercadopago",
          language: "es",
          flow_trace_id: flowTraceId,
          has_payment_id: hasPaymentId,
          has_subscription_id: hasSubscriptionId,
        });
        const query = hasPaymentId
          ? `payment_id=${encodeURIComponent(paymentIdParam as string)}`
          : `subscription_id=${encodeURIComponent(subscriptionIdParam as string)}`;
        const traceParam = flowTraceId ? `&flow_trace_id=${encodeURIComponent(flowTraceId)}` : "";
        const url = `/api/mercadopago/validate-payment?${query}${traceParam}`;
        const res = await fetch(url);
        const data = await res.json() as { valid: boolean; plan?: string; pending?: boolean; status?: string; error?: string };

        if (!res.ok) {
          capturePremiumEvent("premium_success_validation_failed", {
            provider: "mercadopago",
            language: "es",
            flow_trace_id: flowTraceId,
            status: res.status,
            error: data.error || "invalid_payment",
            payment_id: hasPaymentId ? paymentIdParam : undefined,
            subscription_id: hasSubscriptionId ? subscriptionIdParam : undefined,
          });
          setErrorMessage(data.error || "El pago no pudo ser verificado.");
          setValidationState("error");
          return;
        }

        if (data.valid) {
          const resolvedPlan = data.plan === "vip" ? "vip" : (planParam === "vip" ? "vip" : "standard");
          capturePremiumEvent("premium_success_validated", {
            provider: "mercadopago",
            language: "es",
            flow_trace_id: flowTraceId,
            plan_tier: resolvedPlan,
            payment_id: hasPaymentId ? paymentIdParam : undefined,
            subscription_id: hasSubscriptionId ? subscriptionIdParam : undefined,
          });
          // Usamos el plan del servidor si viene en metadata, sino el de la URL
          setValidatedPlan(resolvedPlan);
          setValidationState("valid");
          return;
        }

        // Pago pendiente de acreditación (ej. transferencia bancaria, código QR en efectivo)
        if (data.pending || data.status === "pending" || data.status === "in_process") {
          capturePremiumEvent("premium_success_validation_pending", {
            provider: "mercadopago",
            language: "es",
            flow_trace_id: flowTraceId,
            status: data.status || "pending",
            payment_id: hasPaymentId ? paymentIdParam : undefined,
            subscription_id: hasSubscriptionId ? subscriptionIdParam : undefined,
          });
          setValidationState("pending");
          return;
        }

        capturePremiumEvent("premium_success_validation_failed", {
          provider: "mercadopago",
          language: "es",
          flow_trace_id: flowTraceId,
          status: data.status || "unknown",
          error: data.error || "invalid_payment",
          payment_id: hasPaymentId ? paymentIdParam : undefined,
          subscription_id: hasSubscriptionId ? subscriptionIdParam : undefined,
        });
        setErrorMessage(data.error || "El pago no pudo ser verificado.");
        setValidationState("error");
      } catch {
        capturePremiumEvent("premium_success_validation_failed", {
          provider: "mercadopago",
          language: "es",
          flow_trace_id: flowTraceId,
          error: "network_error",
          payment_id: hasPaymentId ? paymentIdParam : undefined,
          subscription_id: hasSubscriptionId ? subscriptionIdParam : undefined,
        });
        setErrorMessage("Error de conexión al verificar el pago.");
        setValidationState("error");
      }
    };

    validate();
  }, [paymentIdParam, subscriptionIdParam, planParam, flowTraceId, initialPlan]);

  // Fetch the redirect URL configured in the dashboard for this plan tier
  useEffect(() => {
    if (validationState !== "valid") return;
    const fetchRedirectUrl = async () => {
      try {
        const res = await fetch(`/api/membership/success-config?plan_tier=${encodeURIComponent(validatedPlan)}`);
        if (!res.ok) return;
        const data = await res.json() as {
          success_redirect_url?: string | null;
          success_redirect_label?: string | null;
        };
        if (data.success_redirect_url) setRedirectUrl(data.success_redirect_url);
        if (data.success_redirect_label) setRedirectLabel(data.success_redirect_label);
      } catch {
        // Non-fatal — falls back to env var / null below
      }
    };
    void fetchRedirectUrl();
  }, [validationState, validatedPlan]);

  const isVip       = validatedPlan === "vip";
  const gradient    = isVip
    ? "from-amber-400 via-orange-500 to-yellow-500"
    : "from-green-500 via-emerald-500 to-teal-500";
  const glowColor   = isVip ? "bg-amber-500/40"  : "bg-green-500/40";
  const btnGradient = isVip ? "from-amber-500 to-orange-500" : "from-green-500 to-emerald-500";
  // Redirect URL priority:
  // 1. DB-configured URL (dashboard → Planes de Suscripción → URL de éxito)
  // 2. Env var fallback (VITE_SUCCESS_REDIRECT_VIP / VITE_SUCCESS_REDIRECT_STANDARD)
  // 3. null → show generic message instead of CTA button
  const primaryCtaUrl = redirectUrl
    ?? (isVip ? (FALLBACK_VIP_URL ?? null) : (FALLBACK_TELEGRAM_URL ?? null));
  const message       = isVip
    ? "¡Pago recibido! Para activar tu membresía VIP y recibir el acceso, completá tu perfil (cuenta Clerk). El equipo te habilita el link privado después del registro."
    : `¡Tu pago con Mercado Pago fue aprobado! Creá tu cuenta y completá el perfil para activar el acceso al grupo exclusivo de ${BRAND_PERSON_NAME}.`;
  const ctaLabel      = redirectLabel ?? (isVip ? "Ir al portal exclusivo" : "Ingresar al grupo");

  // Post-pago MP → Clerk SignUp → /completar-registro (perfil) → /pending-approval
  const registroUrl = useMemo(() => {
    const parts = [
      "from_mp=true",
      `plan_tier=${encodeURIComponent(validatedPlan)}`,
    ];
    if (validatedPlan === "vip") parts.push("from_vip=1");
    else if (validatedPlan === "standard") parts.push("from_standard=1");
    const identifier = subscriptionIdParam || paymentIdParam;
    if (identifier) parts.push(`subscription_id=${encodeURIComponent(identifier)}`);
    if (flowTraceId) parts.push(`flow_trace_id=${encodeURIComponent(flowTraceId)}`);
    return `${MEMBERSHIP_ROUTE.PORTAL_REGISTRO}?${parts.join("&")}`;
  }, [validatedPlan, subscriptionIdParam, paymentIdParam, flowTraceId]);

  // VIP: auto-redirect a completar perfil Clerk (sin click), igual que PremiumInviteSuccess
  useEffect(() => {
    if (validationState !== "valid" || !isVip || autoRedirected) return;
    const t = window.setTimeout(() => {
      setAutoRedirected(true);
      capturePremiumEvent("premium_success_vip_auto_redirect", {
        provider: "mercadopago",
        language: "es",
        flow_trace_id: flowTraceId,
        plan_tier: validatedPlan,
        destination_url: registroUrl,
        payment_id: paymentIdParam || undefined,
        subscription_id: subscriptionIdParam || undefined,
      });
      navigate(registroUrl);
    }, 3200);
    return () => window.clearTimeout(t);
  }, [
    validationState,
    isVip,
    autoRedirected,
    registroUrl,
    navigate,
    flowTraceId,
    validatedPlan,
    paymentIdParam,
    subscriptionIdParam,
  ]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (validationState === "loading") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black" />
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 text-blue-400 animate-spin mx-auto" />
          <p className="text-gray-400 text-sm">Verificando tu pago...</p>
        </div>
      </div>
    );
  }

  // ── Pendiente ─────────────────────────────────────────────────────────────
  if (validationState === "pending") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500 opacity-10" />
        </div>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="relative inline-flex justify-center w-full">
            <div className="absolute inset-0 blur-2xl bg-yellow-500/30 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full flex items-center justify-center shadow-2xl">
              <Clock className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-yellow-400">Pago en proceso</h1>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-yellow-500/20">
            <p className="text-gray-300 text-sm leading-relaxed mb-2">
              Tu pago está siendo procesado por Mercado Pago. Esto puede tardar unos minutos.
            </p>
            <p className="text-gray-500 text-xs">
              Una vez acreditado, recibirás acceso al grupo. Si el proceso demora más de 24 hs, contactá al soporte.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Error ─────────────────────────────────────────────────────────────────
  if (validationState === "error") {
    return (
      <div
        className="min-h-screen bg-black text-white flex items-center justify-center px-4"
        style={{ fontFamily: FONT_FAMILY }}
      >
        <div className="fixed inset-0 -z-10 bg-black">
          <div className="absolute inset-0 bg-gradient-to-br from-red-500 via-pink-500 to-rose-500 opacity-15" />
        </div>
        <div className="max-w-md w-full text-center space-y-6">
          <div className="relative inline-flex justify-center w-full">
            <div className="absolute inset-0 blur-2xl bg-red-500/30 animate-pulse" />
            <div className="relative w-24 h-24 bg-gradient-to-br from-red-500 to-rose-500 rounded-full flex items-center justify-center shadow-2xl">
              <AlertCircle className="w-12 h-12 text-white" />
            </div>
          </div>
          <h1 className="text-2xl font-bold text-red-400">No pudimos verificar tu pago</h1>
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-red-500/20">
            <p className="text-gray-300 text-sm leading-relaxed mb-2">{errorMessage}</p>
            <p className="text-gray-500 text-xs">
              Si realizaste el pago correctamente, contactá al soporte con tu número de transacción.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Éxito ─────────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen bg-black text-white flex items-center justify-center px-4 select-none"
      style={{ fontFamily: FONT_FAMILY, WebkitUserSelect: "none", WebkitTouchCallout: "none" }}
      onContextMenu={(e) => e.preventDefault()}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
    >
      <div className="fixed inset-0 -z-10 bg-black">
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-20`} />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      <div className="max-w-md w-full text-center space-y-6">
        <div className="relative inline-flex justify-center w-full">
          <div className={`absolute inset-0 blur-2xl ${glowColor} animate-pulse`} />
          <div className={`relative w-24 h-24 bg-gradient-to-br ${gradient} rounded-full flex items-center justify-center shadow-2xl`}>
            {isVip
              ? <Crown className="w-12 h-12 text-white" />
              : <CheckCircle className="w-12 h-12 text-white" />
            }
          </div>
        </div>

        <h1 className={`text-3xl font-bold bg-gradient-to-r ${gradient} bg-clip-text text-transparent`}>
          {isVip ? "¡Muchas gracias!" : "¡Gracias!"}
        </h1>

        <div
          className={`bg-white/5 backdrop-blur-sm rounded-2xl p-6 border ${isVip ? "border-amber-500/30" : "border-white/10"} select-none`}
          onContextMenu={(e) => e.preventDefault()}
        >
          <p className="text-gray-300 text-base leading-relaxed mb-4">{message}</p>
          <p className="text-gray-500 text-xs mb-6">Pago procesado por Mercado Pago</p>

          {/* ── Clerk: crear cuenta + completar perfil (acción principal post-pago) ── */}
          <a
            href={registroUrl}
            className={`w-full py-4 px-6 bg-gradient-to-r ${btnGradient} rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 ui-transition duration-200 flex items-center justify-center gap-2 select-none mb-3`}
            onContextMenu={(e) => e.preventDefault()}
            onClick={() =>
              capturePremiumEvent("premium_success_registro_click", {
                provider: "mercadopago",
                language: "es",
                flow_trace_id: flowTraceId,
                plan_tier: validatedPlan,
                payment_id: paymentIdParam || undefined,
                subscription_id: subscriptionIdParam || undefined,
              })
            }
          >
            <UserPlus className="w-5 h-5 flex-shrink-0" />
            <span>{isVip ? "Completá tu perfil para acceder" : "Crear mi cuenta"}</span>
            <ArrowRight className="w-4 h-4 flex-shrink-0" />
          </a>
          {isVip && !autoRedirected ? (
            <p className="text-gray-500 text-xs text-center mb-4 flex items-center justify-center gap-1.5">
              <Loader2 className="w-3 h-3 animate-spin" />
              Te llevamos a completar tu perfil en un momento…
            </p>
          ) : (
            <p className="text-gray-500 text-xs text-center mb-4">
              {isVip
                ? "Paso 1: cuenta Clerk · Paso 2: completar perfil · Paso 3: te enviamos el acceso."
                : "Creá tu cuenta para activar el acceso y recibir tu invitación privada."}
            </p>
          )}

          {/* Acceso directo solo standard / si admin configuró URL (VIP prioriza Clerk) */}
          {primaryCtaUrl && !isVip && (
            <a
              href={primaryCtaUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full py-3 px-6 rounded-xl border border-white/20 text-gray-400 text-sm font-medium hover:border-white/40 hover:text-white transition-colors duration-200 flex items-center justify-center gap-2 select-none"
              onContextMenu={(e) => e.preventDefault()}
              onClick={() =>
                capturePremiumEvent("premium_success_telegram_click", {
                  provider: "mercadopago",
                  language: "es",
                  flow_trace_id: flowTraceId,
                  plan_tier: validatedPlan,
                  destination_url: primaryCtaUrl,
                  payment_id: paymentIdParam || undefined,
                  subscription_id: subscriptionIdParam || undefined,
                })
              }
            >
              <span>{ctaLabel}</span>
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        <p className="text-gray-600 text-xs select-none">
          {isVip
            ? "Sin completar el perfil en Clerk no podemos asignarte el acceso VIP."
            : "Completá el registro con Clerk para activar tu membresía."}
        </p>
      </div>
    </div>
  );
}
